import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuAccordionItem, MenuActionItem } from '@creditornot/cb-menu';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { useCallback, useEffect, useState, useMemo } from 'react';
import React, { useRef, ReactNode } from 'react';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { Option } from './HierarchyOptions';
import HighlightedText from './HighlightedText';
import SelectableBranchMenuItem from './SelectableBranchMenuItem';
import { allDescendantsAreSelected, searchHierarchy } from './utils';

export type HierarchyOptionT = {
  label: string;
  secondaryContent?: string;
  value: string;
  children: HierarchyOptionT[];
};

type Props = {
  allowBranchSelection?: boolean;
  values: string[];
  hierarchyOption: HierarchyOptionT;
  valuesPath: string[][];
  initialOpenState?: boolean;
  onChange: (value: string, values: string[]) => void;
  caretPosition?: 'left' | 'right';
  parentSelected?: boolean;
  search: string;
  optionsByIdMap: Map<string, Option>;
};

const Label = styled.div`
  white-space: initial;
`;

const SecondaryText = styled(T.Small)<{ selected?: boolean }>`
  margin-inline-start: 8px;
  color: ${({ selected }) =>
    selected
      ? cssThemedValue({
          light: colors.salt84,
          dark: colors.pepper84,
        })
      : cbSemanticCssVars.textSecondary};
`;

const StyledMenuAccordionItem = React.memo(styled(MenuAccordionItem)<{ title: string }>``);

const StyledMenuActionItem = styled(MenuActionItem)<{ title: string }>``;

const useScrollIntoView = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    ref.current.scrollIntoView({
      block: 'center',
    });
  }, []);

  return ref;
};

const HierarchyOption = ({
  allowBranchSelection = false,
  values,
  valuesPath,
  hierarchyOption,
  initialOpenState,
  onChange,
  caretPosition,
  parentSelected = false,
  search,
  optionsByIdMap,
}: Props) => {
  const [firstValue] = values;
  const ref = useScrollIntoView<HTMLButtonElement>();
  const selected = useMemo(
    () => allDescendantsAreSelected(hierarchyOption, values) || parentSelected,
    [hierarchyOption, parentSelected, values],
  );
  const open = useMemo(
    () => (selected ? false : valuesPath.some(paths => paths.includes(hierarchyOption.value))),
    [hierarchyOption.value, selected, valuesPath],
  );
  const [isOpen, setIsOpen] = useState(initialOpenState || open);

  useEffect(() => {
    if (initialOpenState) {
      setIsOpen(initialOpenState);
    }
  }, [initialOpenState]);

  const handleAccordionItemClick = useCallback(() => {
    setIsOpen(s => !s);
  }, []);

  const handleMenuActionItemClick = useCallback(() => {
    const newValues = values.includes(hierarchyOption.value)
      ? values.filter(v => v !== hierarchyOption.value)
      : [...values, hierarchyOption.value];

    onChange(hierarchyOption.value, newValues);
  }, [hierarchyOption.value, onChange, values]);

  const displayValue = useMemo(
    () => (search !== '' ? <HighlightedText text={hierarchyOption.label} search={search} /> : hierarchyOption.label),
    [hierarchyOption.label, search],
  );

  const accordionContent = useMemo(
    () =>
      hierarchyOption.children.reduce<ReactNode[]>((components, childHierarchyOption) => {
        if (search !== '' && !searchHierarchy(childHierarchyOption, search, optionsByIdMap)) {
          return components;
        }

        return [
          ...components,
          <HierarchyOption
            key={childHierarchyOption.value}
            initialOpenState={initialOpenState}
            caretPosition={caretPosition}
            onChange={onChange}
            values={values}
            valuesPath={valuesPath}
            parentSelected={selected}
            hierarchyOption={childHierarchyOption}
            search={search}
            optionsByIdMap={optionsByIdMap}
            allowBranchSelection={allowBranchSelection}
          />,
        ];
      }, []),
    [
      allowBranchSelection,
      caretPosition,
      selected,
      hierarchyOption.children,
      initialOpenState,
      onChange,
      optionsByIdMap,
      search,
      values,
      valuesPath,
    ],
  );

  if (allowBranchSelection) {
    return (
      <SelectableBranchMenuItem
        accordionContent={isOpen ? accordionContent : undefined}
        caretPosition={caretPosition}
        key={hierarchyOption.value}
        hierarchyOption={hierarchyOption}
        isOpen={isOpen}
        onAccordionItemClick={handleAccordionItemClick}
        onChange={onChange}
        optionsByIdMap={optionsByIdMap}
        checked={selected}
        values={values}
        valuesPath={valuesPath}
        ref={firstValue === hierarchyOption.value ? ref : undefined}
      />
    );
  }

  if (!hierarchyOption.children.length) {
    return (
      <StyledMenuActionItem
        title={hierarchyOption.label}
        key={hierarchyOption.value}
        variant="black"
        ref={firstValue === hierarchyOption.value ? ref : undefined}
        onClick={handleMenuActionItemClick}
        selected={selected}
      >
        <Label>
          {displayValue}
          {!!hierarchyOption.secondaryContent && (
            <SecondaryText selected={selected}>({hierarchyOption.secondaryContent})</SecondaryText>
          )}
        </Label>
      </StyledMenuActionItem>
    );
  }

  return (
    <StyledMenuAccordionItem
      title={hierarchyOption.label}
      key={hierarchyOption.value}
      open={isOpen}
      onClick={handleAccordionItemClick}
      menuItemRef={firstValue === hierarchyOption.value ? ref : undefined}
      caretPosition={caretPosition}
      accordionContent={isOpen && accordionContent}
    >
      <Label>{displayValue}</Label>
    </StyledMenuAccordionItem>
  );
};

export default React.memo(HierarchyOption);
